import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection, onSnapshot, doc, getDoc, getDocs, setDoc, writeBatch, addDoc, updateDoc, serverTimestamp,
  query, where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Box, Typography, Paper, Button, TextField, Alert, CircularProgress, Chip, IconButton, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Checkbox, FormControlLabel, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButton, ToggleButtonGroup, InputAdornment,
  Tooltip, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import Layout from '../components/Layout';
import { localToday } from '../utils/dates';
import { logError } from '../utils/errorLogger';
import {
  COURTS, DAY_SHORT, KINDS, MEET_FORMATS, SCHOOL_PALETTE, toMins, fromMins, clock, addDays,
  weekStart, seasonWeeks, seriesDates, courtHours, dropInCents, hoursBySchool, conflicts, gridSlots,
  sessionAt, reservationFor, dayOfWeek, calendarFromWeek1, CALENDAR_FIELDS, weekLabel, dayMilestone,
} from '../utils/schoolSchedule';

// /admin/school-schedule — the high-school season, tracked the way the 2026
// OCPS Court Schedule spreadsheet did: practices by week, meet days with their
// 3 or 6 courts, and drop-in dates for schools without a season contract.
// Every session also writes a detail-free "School use" reservation, so the
// courts are blocked in member booking and shown as taken on the calendar.

const navy = '#1E3A5F';
const WEEKDAYS = [1, 2, 3, 4, 5];
const BILLING = {
  contract: 'Season contract',
  invoice: 'Invoice',
  stripe: 'Card (Stripe link)',
  none: 'No charge',
};
const BILL_STATUS = {
  unpaid: { label: 'Unpaid', color: 'error' },
  invoiced: { label: 'Invoiced', color: 'warning' },
  link_sent: { label: 'Link sent', color: 'warning' },
  paid: { label: 'Paid', color: 'success' },
};

const DEFAULT_SETTINGS = {
  season: { days: [1, 2, 3, 4], windowStart: '14:00', windowEnd: '17:30' },
  dropInRate: 30,
  schools: [],
  calendar: {},
};

const fmtMoney = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const shortDate = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const longDate = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '');
const newId = () => doc(collection(db, 'schoolSessions')).id;

// Preseason has no fixed start: it begins with the first school session in the
// eight weeks before first practice, if there is one.
function preseasonStart(sessions, cal) {
  if (!cal?.firstPractice) return '';
  const from = addDays(cal.firstPractice, -56);
  const first = sessions.find(s => s.date >= from && s.date < cal.firstPractice);
  return first ? weekStart(first.date) : '';
}

function blankSession(kind, settings, patch = {}) {
  const base = {
    kind, schoolCode: '', date: localToday(), startTime: '15:00', endTime: '17:30',
    courts: ['court-1', 'court-2'], notes: '',
    meet: { format: 'dual', visitors: '', arrive: '' },
    billing: { mode: kind === 'dropin' ? 'invoice' : 'contract', rate: settings.dropInRate, status: 'unpaid' },
    repeat: { on: false, days: [1, 2], until: settings.calendar?.lastGamesEnd || '', skip: '' },
  };
  if (kind === 'meet') Object.assign(base, { startTime: '15:00', endTime: '18:00', courts: COURTS.slice(0, 3) });
  return { ...base, ...patch };
}

// readOnly is the high-school coaches' view: every school's practices and
// meets, so they can work court time out between them, and nothing that edits
// or bills.
// mySchool is a coach's own school code (users.hsSchoolCode), outlined on the
// grid so their sessions stand out among everyone's.
export default function AdminSchoolSchedule({ embedded = false, readOnly = false, mySchool = '' }) {
  // Inside the Schools page tabs the page around it supplies the Layout.
  const Wrap = embedded ? React.Fragment : Layout;
  const narrow = useMediaQuery('(max-width:900px)');
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [sessions, setSessions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [week, setWeek] = useState(weekStart(searchParams.get('date') || localToday()));
  const [editing, setEditing] = useState(null); // { id|null, form }

  useEffect(() => {
    getDoc(doc(db, 'settings', 'schoolSchedule')).then(s => {
      if (s.exists()) {
        const d = s.data();
        setSettings({ ...DEFAULT_SETTINGS, ...d, season: { ...DEFAULT_SETTINGS.season, ...(d.season || {}) }, calendar: d.calendar || {} });
      }
    }).catch(() => {});
    if (!readOnly) {
      getDocs(collection(db, 'customers'))
        .then(snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.archived)))
        .catch(() => {});
    }
    const unsub = onSnapshot(collection(db, 'schoolSessions'), snap => {
      setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime)));
      setLoading(false);
    }, err => {
      setError(err.code === 'permission-denied' ? 'Admins and high-school coaches only.' : 'Could not load the school schedule.');
      setLoading(false);
    });
    return unsub;
  }, [readOnly]);

  const schoolBy = useMemo(() => Object.fromEntries(settings.schools.map(s => [s.code, s])), [settings.schools]);
  const colorOf = (code) => schoolBy[code]?.color || '#E5E7E9';

  const saveSettings = async (next) => {
    try {
      await setDoc(doc(db, 'settings', 'schoolSchedule'), next);
      setSettings(next);
      setSuccess('Settings saved.');
    } catch (e) {
      setError(`Could not save settings: ${e.message}`);
    }
  };

  const openNew = (kind, patch) => { if (!readOnly) setEditing({ id: null, form: blankSession(kind, settings, patch) }); };
  const openEdit = (s) => !readOnly && setEditing({
    id: s.id,
    form: {
      ...blankSession(s.kind, settings), ...s,
      meet: { format: 'dual', arrive: '', ...(s.meet || {}), visitors: (s.meet?.visitors || []).join(', ') },
      billing: { ...blankSession(s.kind, settings).billing, ...(s.billing || {}) },
    },
  });

  if (loading) return <Wrap><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Wrap>;

  return (
    <Wrap>
      <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <EventNoteIcon sx={{ color: navy, fontSize: 32 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: navy }}>School Schedule</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              {settings.calendar?.week1 ? `Week 1 ${shortDate(settings.calendar.week1)}` : 'Set week 1 in Settings'}
              {' · '}practices, meets and drop-ins
              {mySchool && ` · your school: ${schoolBy[mySchool]?.name || mySchool}, outlined`}
            </Typography>
          </Box>
          {!readOnly && <>
            <Button startIcon={<AddIcon />} onClick={() => openNew('practice', { repeat: { on: true, days: [1, 2], until: settings.calendar?.lastGamesEnd || '', skip: '' }, date: settings.calendar?.firstPractice || localToday() })}>Practice</Button>
            <Button startIcon={<AddIcon />} onClick={() => openNew('meet')}>Meet</Button>
            <Button startIcon={<AddIcon />} onClick={() => openNew('dropin')}>Drop-in</Button>
          </>}
        </Box>

        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
        {error && !editing && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {!readOnly && <WindowRequests onMessage={setSuccess} onError={setError} />}
        {!readOnly && settings.schools.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>Add the schools (name, short code like ERHS) under <strong>Settings</strong> first.</Alert>
        )}

        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
          <Tab label="Week" />
          <Tab label="Season" />
          <Tab label="Meets" />
          {!readOnly && <Tab label="Drop-ins" />}
          {!readOnly && <Tab label="Settings" />}
        </Tabs>

        {tab === 0 && (
          <WeekGrid week={week} setWeek={setWeek} sessions={sessions} settings={settings} colorOf={colorOf} mySchool={mySchool}
            onOpen={openEdit} onNew={(date, court, slot) => openNew('practice', {
              date, courts: [court], startTime: slot, endTime: fromMins(Math.min(toMins(slot) + 120, 22 * 60)),
            })} />
        )}
        {tab === 1 && (
          <SeasonTable sessions={sessions} settings={settings} colorOf={colorOf}
            onWeek={(w) => { setWeek(w); setTab(0); }} />
        )}
        {tab === 2 && <MeetList sessions={sessions.filter(s => s.kind === 'meet')} schoolBy={schoolBy} onOpen={openEdit} />}
        {tab === 3 && !readOnly && (
          <DropInList sessions={sessions.filter(s => s.kind === 'dropin')} schoolBy={schoolBy} customers={customers}
            onOpen={openEdit} onMessage={setSuccess} onError={setError} />
        )}
        {tab === 4 && !readOnly && <SettingsPanel settings={settings} customers={customers} onSave={saveSettings} />}
      </Box>

      {editing && (
        <SessionDialog editing={editing} setEditing={setEditing} sessions={sessions} settings={settings}
          narrow={narrow} onMessage={setSuccess} />
      )}
    </Wrap>
  );
}

// ── Gold requests in the school window ────────────────────────────────────────
// createMemberBooking saves a Gold member's booking inside the window as
// 'pending_approval'. It holds the court until it's decided here.
function WindowRequests({ onMessage, onError }) {
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState('');
  useEffect(() => onSnapshot(
    query(collection(db, 'memberBookings'), where('status', '==', 'pending_approval')),
    snap => setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime))),
    () => {}), []);
  if (!requests.length) return null;
  const decide = async (r, approve) => {
    let note = '';
    if (!approve) {
      note = window.prompt('Decline this request? Optional note for the member:', '');
      if (note === null) return;
    }
    setBusy(r.id);
    try {
      const { data } = await httpsCallable(getFunctions(), 'decideMemberBookingApproval')({ bookingId: r.id, approve, note });
      onMessage(`${approve ? 'Approved' : 'Declined'} ${r.memberName || 'the request'}${data.emailed ? ', and emailed them' : ' (no email on file)'}.`);
    } catch (e) {
      onError(`Could not ${approve ? 'approve' : 'decline'}: ${e.message}`);
    }
    setBusy('');
  };
  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid #E67E22' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Court requests in high-school hours ({requests.length})
      </Typography>
      {requests.map(r => (
        <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.5 }}>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 220 }}>
            <strong>{r.memberName || 'Member'}</strong> · {longDate(r.date)} {clock(r.startTime)}–{clock(r.endTime)}
            {' · '}{(r.courts || []).map(c => c.replace('court-', 'Court ')).join(', ')}
          </Typography>
          <Button size="small" variant="contained" color="success" disabled={busy === r.id} onClick={() => decide(r, true)}>Approve</Button>
          <Button size="small" color="error" disabled={busy === r.id} onClick={() => decide(r, false)}>Decline</Button>
        </Box>
      ))}
    </Paper>
  );
}

// ── Week ──────────────────────────────────────────────────────────────────────
function WeekGrid({ week, setWeek, sessions, settings, colorOf, onOpen, onNew, mySchool = '' }) {
  const dates = WEEKDAYS.map((d, i) => addDays(week, i));
  const inWeek = sessions.filter(s => s.date >= dates[0] && s.date <= dates[4]);
  const slots = gridSlots(inWeek, settings.season.windowStart || '14:00', '18:30');
  const meets = inWeek.filter(s => s.kind === 'meet');
  const { by, total } = hoursBySchool(inWeek);
  const cell = { p: 0, border: '1px solid #e3e3e3', minWidth: 34, height: 26, fontSize: 11, textAlign: 'center' };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => setWeek(addDays(week, -7))}><ChevronLeftIcon /></IconButton>
        <Typography sx={{ fontWeight: 700 }}>Week of {longDate(week)}</Typography>
        {weekLabel(week, settings.calendar, preseasonStart(sessions, settings.calendar)) && (
          <Chip size="small" color="primary" variant="outlined" label={weekLabel(week, settings.calendar, preseasonStart(sessions, settings.calendar))} />
        )}
        <IconButton onClick={() => setWeek(addDays(week, 7))}><ChevronRightIcon /></IconButton>
        <Button size="small" onClick={() => setWeek(weekStart(localToday()))}>This week</Button>
        <Box sx={{ flex: 1 }} />
        {Object.entries(by).map(([code, h]) => (
          <Chip key={code} size="small" label={`${code} ${h}h`} sx={{ backgroundColor: colorOf(code) }} />
        ))}
        <Typography variant="body2" sx={{ color: '#666' }}>{total} court-hours</Typography>
      </Box>

      <Paper elevation={1} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ borderCollapse: 'collapse', '& td, & th': { lineHeight: 1.1 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...cell, minWidth: 64 }} />
              {dates.map(d => (
                <TableCell key={d} colSpan={6} sx={{ ...cell, fontWeight: 700, backgroundColor: dayMilestone(d, settings.calendar) ? '#FEF5E7' : '#f4f6f8', py: 0.5 }}>
                  {DAY_SHORT[dayOfWeek(d)]} {shortDate(d)}
                  {dayMilestone(d, settings.calendar) && (
                    <Box component="span" sx={{ display: 'block', fontWeight: 400, color: '#9A5B00' }}>{dayMilestone(d, settings.calendar)}</Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={cell}>Time</TableCell>
              {dates.map(d => COURTS.map(c => (
                <TableCell key={d + c} sx={{ ...cell, color: '#888' }}>{c.replace('court-', '')}</TableCell>
              )))}
            </TableRow>
          </TableHead>
          <TableBody>
            {slots.map(slot => (
              <TableRow key={slot}>
                <TableCell sx={{ ...cell, whiteSpace: 'nowrap', px: 0.5, color: '#555' }}>{clock(slot)}</TableCell>
                {dates.map(d => COURTS.map(c => {
                  const s = sessionAt(inWeek, d, c, slot);
                  const first = s && s.startTime <= slot && toMins(slot) - toMins(s.startTime) < 30;
                  return (
                    <TableCell key={d + c} sx={{ ...cell, cursor: 'pointer', backgroundColor: s ? colorOf(s.schoolCode) : 'transparent',
                      ...(s && mySchool && s.schoolCode === mySchool ? { outline: '2px solid #1E3A5F', outlineOffset: '-2px' } : {}),
                      '&:hover': { outline: '2px solid #2E6DA4' } }}
                      onClick={() => (s ? onOpen(s) : onNew(d, c, slot))}>
                      {first ? (
                        <Tooltip title={`${s.schoolCode} · ${KINDS[s.kind]?.label} · ${clock(s.startTime)}–${clock(s.endTime)}${s.kind === 'meet' && s.meet?.visitors?.length ? ` · v ${s.meet.visitors.join(', ')}` : ''}`}>
                          <span style={{ fontWeight: 700 }}>{s.schoolCode}{s.kind === 'meet' ? '*' : ''}</span>
                        </Tooltip>
                      ) : ''}
                    </TableCell>
                  );
                }))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
        Click an empty cell to add a practice there, or a filled one to edit it. * = meet.
      </Typography>

      {meets.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Meets this week</Typography>
          {meets.map(m => (
            <Typography key={m.id} variant="body2" sx={{ cursor: 'pointer' }} onClick={() => onOpen(m)}>
              {longDate(m.date)} · <strong>{m.schoolCode}</strong>{m.meet?.visitors?.length ? ` v ${m.meet.visitors.join(', ')}` : ''}
              {' · '}{MEET_FORMATS[m.meet?.format]?.label || 'Meet'}, {m.courts.length} courts
              {m.meet?.arrive ? ` · arrive ${clock(m.meet.arrive)}` : ''} · {clock(m.startTime)}–{clock(m.endTime)}
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
}

// ── Season ────────────────────────────────────────────────────────────────────
function SeasonTable({ sessions, settings, colorOf, onWeek }) {
  // From preseason (or first practice when there's none) through states.
  const c = settings.calendar || {};
  const pre = preseasonStart(sessions, c);
  const weeks = seasonWeeks(pre || c.firstPractice, c.statesEnd);
  if (!weeks.length) return <Alert severity="info">Set week 1 in Settings to see the season.</Alert>;
  const inSeason = sessions.filter(s => s.date >= weeks[0] && s.date <= addDays(weeks[weeks.length - 1], 6));
  const codes = [...new Set(inSeason.map(s => s.schoolCode))].sort();
  const grand = hoursBySchool(inSeason);

  const cal = settings.calendar || {};
  return (
    <Box>
    {cal.week1 ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {CALENDAR_FIELDS.map(c => cal[c.key] && (
          <Chip key={c.key} size="small" variant="outlined"
            label={`${c.label}: ${shortDate(cal[c.key])}${c.end && cal[c.end] ? ` – ${shortDate(cal[c.end])}` : ''}`} />
        ))}
      </Box>
    ) : (
      <Alert severity="info" sx={{ mb: 1.5 }}>Set week 1 in Settings to fill in the high school calendar.</Alert>
    )}
    <Paper elevation={1} sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Week</TableCell>
            {WEEKDAYS.map(d => <TableCell key={d}>{DAY_SHORT[d]}</TableCell>)}
            {codes.map(c => <TableCell key={c} align="right" sx={{ backgroundColor: colorOf(c) }}>{c}</TableCell>)}
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {weeks.map(w => {
            const wk = inSeason.filter(s => s.date >= w && s.date <= addDays(w, 6));
            const { by, total } = hoursBySchool(wk);
            return (
              <TableRow key={w} hover sx={{ cursor: 'pointer' }} onClick={() => onWeek(w)}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {weekLabel(w, settings.calendar, pre) || ' '}
                  <Typography variant="caption" component="span" sx={{ display: 'block', color: '#888' }}>{shortDate(w)}</Typography>
                </TableCell>
                {WEEKDAYS.map((d, i) => {
                  const day = wk.filter(s => s.date === addDays(w, i));
                  return (
                    <TableCell key={d} sx={{ fontSize: 12 }}>
                      {day.map(s => (
                        <Box key={s.id} component="span" sx={{ display: 'inline-block', px: 0.5, mr: 0.5, mb: 0.25, borderRadius: 0.5,
                          backgroundColor: colorOf(s.schoolCode), fontWeight: s.kind === 'meet' ? 700 : 400 }}>
                          {s.schoolCode}{s.kind === 'meet' ? '*' : ''} {s.courts.length}c
                        </Box>
                      ))}
                    </TableCell>
                  );
                })}
                {codes.map(c => <TableCell key={c} align="right">{by[c] || ''}</TableCell>)}
                <TableCell align="right" sx={{ fontWeight: 600 }}>{total || ''}</TableCell>
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Season</TableCell>
            <TableCell colSpan={5} />
            {codes.map(c => <TableCell key={c} align="right" sx={{ fontWeight: 700 }}>{grand.by[c] || 0}</TableCell>)}
            <TableCell align="right" sx={{ fontWeight: 700 }}>{grand.total}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', p: 1 }}>
        Court-hours (courts × hours). “3c” = three courts. * = meet. Click a week to open it.
      </Typography>
    </Paper>
    </Box>
  );
}

// ── Meets ─────────────────────────────────────────────────────────────────────
function MeetList({ sessions, schoolBy, onOpen }) {
  if (!sessions.length) return <Alert severity="info">No meets yet. Add one with “+ Meet”.</Alert>;
  return (
    <Paper elevation={1} sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell><TableCell>Home</TableCell><TableCell>Visiting</TableCell>
            <TableCell>Format</TableCell><TableCell>Courts</TableCell><TableCell>Arrive</TableCell>
            <TableCell>Play</TableCell><TableCell>Billing</TableCell><TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map(m => (
            <TableRow key={m.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(m)}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{longDate(m.date)}</TableCell>
              <TableCell>{schoolBy[m.schoolCode]?.name || m.schoolCode}</TableCell>
              <TableCell>{(m.meet?.visitors || []).join(', ')}</TableCell>
              <TableCell>{MEET_FORMATS[m.meet?.format]?.label || ''}</TableCell>
              <TableCell>{m.courts.map(c => c.replace('court-', '')).join(', ')}</TableCell>
              <TableCell>{clock(m.meet?.arrive)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{clock(m.startTime)}–{clock(m.endTime)}</TableCell>
              <TableCell>{BILLING[m.billing?.mode] || ''}</TableCell>
              <TableCell sx={{ fontSize: 12, color: '#666' }}>{m.notes}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

// ── Drop-ins ──────────────────────────────────────────────────────────────────
function DropInList({ sessions, schoolBy, customers, onOpen, onMessage, onError }) {
  const [busyId, setBusyId] = useState(null);
  const [linkFor, setLinkFor] = useState(null); // { session, email }

  const invoice = async (s) => {
    setBusyId(s.id);
    try {
      const school = schoolBy[s.schoolCode] || {};
      const cust = customers.find(c => c.id === school.customerId);
      const ref = await addDoc(collection(db, 'customerInvoices'), {
        ...(cust ? { customerId: cust.id } : {}),
        customerName: cust?.organizationName || school.name || s.schoolCode,
        description: `Court rental ${s.date} ${clock(s.startTime)}–${clock(s.endTime)}, ${s.courts.length} court${s.courts.length === 1 ? '' : 's'}`,
        invoiceDate: localToday(),
        amount: (s.billing?.amountCents || 0) / 100,
        paid: false,
        schoolSessionId: s.id,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'schoolSessions', s.id), {
        'billing.mode': 'invoice', 'billing.status': 'invoiced', 'billing.invoiceId': ref.id, updatedAt: serverTimestamp(),
      });
      onMessage(`Invoice created for ${school.name || s.schoolCode}${cust ? '' : ' — link the school to a customer in Settings to see it on their account'}.`);
    } catch (e) {
      onError(`Invoice failed: ${e.message}`);
    }
    setBusyId(null);
  };

  const markPaid = async (s) => {
    setBusyId(s.id);
    try {
      await updateDoc(doc(db, 'schoolSessions', s.id), { 'billing.status': 'paid', 'billing.paidAt': serverTimestamp(), updatedAt: serverTimestamp() });
      if (s.billing?.invoiceId) {
        await updateDoc(doc(db, 'customerInvoices', s.billing.invoiceId), { paid: true, paidDate: localToday() });
      }
      onMessage('Marked paid.');
    } catch (e) {
      onError(e.message);
    }
    setBusyId(null);
  };

  const sendLink = async () => {
    const { session: s, email } = linkFor;
    setBusyId(s.id);
    try {
      const res = await httpsCallable(getFunctions(), 'createSchoolSessionCheckout')({ sessionId: s.id, email: email.trim() });
      try { await navigator.clipboard.writeText(res.data.url); } catch { /* shown below */ }
      onMessage(`Payment link ${email.trim() ? `emailed to ${email.trim()} and ` : ''}copied: ${res.data.url}`);
      setLinkFor(null);
    } catch (e) {
      onError(e.message);
    }
    setBusyId(null);
  };

  if (!sessions.length) return <Alert severity="info">No drop-ins yet. Add one with “+ Drop-in”.</Alert>;
  return (
    <Paper elevation={1} sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell><TableCell>School</TableCell><TableCell>Time</TableCell>
            <TableCell>Courts</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map(s => {
            const st = BILL_STATUS[s.billing?.status || 'unpaid'] || BILL_STATUS.unpaid;
            const charge = s.billing?.mode === 'invoice' || s.billing?.mode === 'stripe';
            return (
              <TableRow key={s.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => onOpen(s)}>{longDate(s.date)}</TableCell>
                <TableCell>{schoolBy[s.schoolCode]?.name || s.schoolCode}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{clock(s.startTime)}–{clock(s.endTime)}</TableCell>
                <TableCell>{s.courts.length}</TableCell>
                <TableCell align="right">{charge ? fmtMoney(s.billing?.amountCents) : BILLING[s.billing?.mode]}</TableCell>
                <TableCell>{charge && <Chip size="small" color={st.color} label={st.label} />}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {charge && s.billing?.status !== 'paid' && (
                    <>
                      {!s.billing?.invoiceId && <Button size="small" disabled={busyId === s.id} onClick={() => invoice(s)}>Invoice</Button>}
                      <Button size="small" disabled={busyId === s.id} onClick={() => setLinkFor({ session: s, email: '' })}>Card link</Button>
                      <Button size="small" disabled={busyId === s.id} onClick={() => markPaid(s)}>Mark paid</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={!!linkFor} onClose={() => setLinkFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Card payment link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {linkFor && `${fmtMoney(linkFor.session.billing?.amountCents)} for ${longDate(linkFor.session.date)}.`}
            {' '}The link is copied for you; add an email to also send it.
          </Typography>
          <TextField fullWidth size="small" label="Email (optional)" type="email" value={linkFor?.email || ''}
            onChange={e => setLinkFor(l => ({ ...l, email: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={sendLink} disabled={!!busyId} sx={{ backgroundColor: navy }}>Create link</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ── Settings ──────────────────────────────────────────────────────────────────
function SettingsPanel({ settings, customers, onSave }) {
  const [form, setForm] = useState(settings);
  useEffect(() => setForm(settings), [settings]);
  const season = form.season;
  const setSeason = (patch) => setForm(f => ({ ...f, season: { ...f.season, ...patch } }));
  const setSchool = (i, patch) => setForm(f => ({ ...f, schools: f.schools.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const cal = form.calendar || {};
  // A new week 1 re-fills every date from it; each can still be edited after.
  const setWeek1 = (v) => setForm(f => ({ ...f, calendar: v ? calendarFromWeek1(v) : {} }));
  const setCal = (patch) => setForm(f => ({ ...f, calendar: { ...f.calendar, ...patch } }));
  const codes = form.schools.map(s => s.code.trim().toUpperCase());
  const dupe = codes.some((c, i) => c && codes.indexOf(c) !== i);

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>High school calendar</Typography>
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
          Set week 1 (the first week of games) and the rest fill in from it. Change any date that differs this year.
        </Typography>
        <Grid container spacing={1.5} alignItems="center">
          <Grid size={{ xs: 12, sm: 5 }}><Typography variant="body2" sx={{ fontWeight: 600 }}>Week 1 (first games)</Typography></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="date" value={cal.week1 || ''}
            onChange={e => setWeek1(e.target.value)} /></Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            {cal.week1 && <Button size="small" onClick={() => setWeek1(cal.week1)}>Re-fill from week 1</Button>}
          </Grid>
          {cal.week1 && CALENDAR_FIELDS.filter(c => c.key !== 'week1').map(c => (
            <React.Fragment key={c.key}>
              <Grid size={{ xs: 12, sm: 5 }}><Typography variant="body2">{c.label}</Typography></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="date" value={cal[c.key] || ''}
                label={c.end ? 'From' : undefined} InputLabelProps={{ shrink: true }}
                onChange={e => setCal({ [c.key]: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                {c.end && <TextField fullWidth size="small" type="date" label="To" value={cal[c.end] || ''}
                  InputLabelProps={{ shrink: true }} onChange={e => setCal({ [c.end]: e.target.value })} />}
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>School window</Typography>
        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
          Shown on the calendar as “Reserved for high schools” from first practice to the last regional date, except
          spring break. Members can't book in it; Gold members can request it, and each request waits for approval above.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="time" label="Window starts" value={season.windowStart}
            onChange={e => setSeason({ windowStart: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="time" label="Window ends" value={season.windowEnd}
            onChange={e => setSeason({ windowEnd: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <ToggleButtonGroup size="small" value={season.days} onChange={(e, v) => setSeason({ days: [...v].sort() })}>
              {DAY_SHORT.map((d, i) => <ToggleButton key={d} value={i} sx={{ px: 1.2 }}>{d}</ToggleButton>)}
            </ToggleButtonGroup>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" type="number" label="Drop-in rate per court-hour" value={form.dropInRate}
            onChange={e => setForm(f => ({ ...f, dropInRate: Number(e.target.value) }))}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Schools</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setForm(f => ({
            ...f, schools: [...f.schools, { code: '', name: '', color: SCHOOL_PALETTE[f.schools.length % SCHOOL_PALETTE.length], customerId: '' }],
          }))}>Add school</Button>
        </Box>
        {form.schools.map((s, i) => (
          <Grid container spacing={1} key={i} alignItems="center" sx={{ mb: 1 }}>
            <Grid size={{ xs: 3, sm: 2 }}><TextField fullWidth size="small" label="Code" value={s.code}
              onChange={e => setSchool(i, { code: e.target.value.toUpperCase() })} /></Grid>
            <Grid size={{ xs: 9, sm: 4 }}><TextField fullWidth size="small" label="Name" value={s.name}
              onChange={e => setSchool(i, { name: e.target.value })} /></Grid>
            <Grid size={{ xs: 8, sm: 4 }}>
              <TextField select fullWidth size="small" label="Billing customer" value={s.customerId || ''}
                onChange={e => setSchool(i, { customerId: e.target.value })}>
                <MenuItem value="">(none)</MenuItem>
                {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.organizationName || `${c.firstName} ${c.lastName}`}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 2, sm: 1 }}>
              <input type="color" value={s.color} onChange={e => setSchool(i, { color: e.target.value })}
                style={{ width: 36, height: 32, border: 'none', background: 'none' }} aria-label="Color" />
            </Grid>
            <Grid size={{ xs: 2, sm: 1 }}>
              <IconButton size="small" onClick={() => setForm(f => ({ ...f, schools: f.schools.filter((_, j) => j !== i) }))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Typography variant="caption" sx={{ color: '#888' }}>
          The billing customer (from Court Rentals → Customers) is who drop-in invoices go to.
        </Typography>
      </Paper>

      {dupe && <Alert severity="error" sx={{ mb: 1 }}>Two schools share a code.</Alert>}
      <Button variant="contained" disabled={dupe || form.schools.some(s => !s.code.trim() || !s.name.trim())}
        onClick={() => onSave({ ...form, schools: form.schools.map(s => ({ ...s, code: s.code.trim().toUpperCase(), name: s.name.trim() })) })}
        sx={{ backgroundColor: navy }}>Save settings</Button>
    </Box>
  );
}

// ── Add / edit ────────────────────────────────────────────────────────────────
function SessionDialog({ editing, setEditing, sessions, settings, narrow, onMessage }) {
  const f = editing.form;
  const isNew = !editing.id;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (patch) => setEditing(ed => ({ ...ed, form: { ...ed.form, ...patch } }));
  const setMeet = (patch) => set({ meet: { ...f.meet, ...patch } });
  const setBilling = (patch) => set({ billing: { ...f.billing, ...patch } });
  const setRepeat = (patch) => set({ repeat: { ...f.repeat, ...patch } });

  const repeating = isNew && f.kind === 'practice' && f.repeat.on;
  const skip = String(f.repeat.skip || '').split(/[\s,]+/).filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d));
  const dates = repeating ? seriesDates({ from: f.date, to: f.repeat.until, days: f.repeat.days, skip }) : [f.date];
  const amountCents = dropInCents(f, f.billing.rate);
  const charged = f.billing.mode === 'invoice' || f.billing.mode === 'stripe';
  const clashes = dates.flatMap(d => conflicts(sessions, { ...f, id: editing.id, date: d }));
  const valid = f.schoolCode && f.date && f.courts.length && toMins(f.endTime) > toMins(f.startTime) && dates.length > 0
    && (!repeating || f.repeat.days.length);

  const pickFormat = (format) => {
    const n = MEET_FORMATS[format]?.courts || 3;
    const hours = MEET_FORMATS[format]?.hours;
    setEditing(ed => ({ ...ed, form: {
      ...ed.form, meet: { ...ed.form.meet, format }, courts: COURTS.slice(0, n),
      ...(hours ? { endTime: fromMins(Math.min(toMins(ed.form.startTime) + hours * 60, 22 * 60)) } : {}),
    } }));
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const batch = writeBatch(db);
      const seriesId = repeating ? newId() : (f.seriesId || null);
      const ids = [];
      for (const date of dates) {
        const id = isNew ? newId() : editing.id;
        const session = {
          kind: f.kind, schoolCode: f.schoolCode, schoolName: settings.schools.find(s => s.code === f.schoolCode)?.name || f.schoolCode,
          date, startTime: f.startTime, endTime: f.endTime, courts: [...f.courts].sort(), notes: f.notes || '',
          seriesId,
          meet: f.kind === 'meet' ? {
            format: f.meet.format, arrive: f.meet.arrive || '',
            visitors: String(f.meet.visitors || '').split(',').map(v => v.trim()).filter(Boolean),
          } : null,
          billing: {
            ...f.billing,
            mode: f.billing.mode, rate: Number(f.billing.rate) || 0,
            amountCents: charged ? amountCents : 0,
            status: f.billing.status || 'unpaid',
          },
          updatedAt: serverTimestamp(),
          ...(isNew ? { createdAt: serverTimestamp() } : {}),
        };
        batch.set(doc(db, 'schoolSessions', id), session, { merge: !isNew });
        batch.set(doc(db, 'reservations', id), { ...reservationFor({ ...session, id }), updatedAt: serverTimestamp() });
        ids.push(id);
      }
      await batch.commit();
      onMessage(isNew ? `Added ${ids.length} session${ids.length === 1 ? '' : 's'}.` : 'Saved.');
      setEditing(null);
    } catch (e) {
      logError('AdminSchoolSchedule.save', e.message, e.stack || '');
      setError(`Save failed: ${e.message}`);
    }
    setBusy(false);
  };

  // Delete this one, or this and every later date in its practice series.
  const remove = async (laterToo) => {
    setBusy(true);
    try {
      const doomed = laterToo && f.seriesId
        ? sessions.filter(s => s.seriesId === f.seriesId && s.date >= f.date)
        : [{ id: editing.id }];
      for (let i = 0; i < doomed.length; i += 200) {
        const batch = writeBatch(db);
        doomed.slice(i, i + 200).forEach(s => {
          batch.delete(doc(db, 'schoolSessions', s.id));
          batch.delete(doc(db, 'reservations', s.id));
        });
        await batch.commit();
      }
      onMessage(`Removed ${doomed.length} session${doomed.length === 1 ? '' : 's'}.`);
      setEditing(null);
    } catch (e) {
      setError(`Delete failed: ${e.message}`);
    }
    setBusy(false);
  };

  return (
    <Dialog open fullScreen={narrow} maxWidth="md" fullWidth onClose={() => !busy && setEditing(null)}>
      <DialogTitle>{isNew ? 'Add' : 'Edit'} {KINDS[f.kind]?.label.toLowerCase()}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField select fullWidth size="small" label="Type" value={f.kind} disabled={!isNew}
              onChange={e => setEditing({ id: null, form: blankSession(e.target.value, settings, { schoolCode: f.schoolCode, date: f.date }) })}>
              {Object.entries(KINDS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField select fullWidth size="small" label={f.kind === 'meet' ? 'Home school' : 'School'} value={f.schoolCode}
              onChange={e => set({ schoolCode: e.target.value })}>
              {settings.schools.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
            </TextField>
          </Grid>

          {f.kind === 'meet' && (
            <>
              <Grid size={{ xs: 12, sm: 8 }}><TextField fullWidth size="small" label="Visiting teams (comma separated)"
                value={f.meet.visitors} onChange={e => setMeet({ visitors: e.target.value })} /></Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField select fullWidth size="small" label="Format" value={f.meet.format} onChange={e => pickFormat(e.target.value)}>
                  {Object.entries(MEET_FORMATS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" type="date" label={repeating ? 'First date' : 'Date'}
            value={f.date} onChange={e => set({ date: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          {f.kind === 'meet' && (
            <Grid size={{ xs: 4, sm: 2 }}><TextField fullWidth size="small" type="time" label="Arrive" value={f.meet.arrive}
              onChange={e => setMeet({ arrive: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          )}
          <Grid size={{ xs: 4, sm: f.kind === 'meet' ? 3 : 4 }}><TextField fullWidth size="small" type="time" label="Start" value={f.startTime}
            onChange={e => set({ startTime: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid size={{ xs: 4, sm: f.kind === 'meet' ? 3 : 4 }}><TextField fullWidth size="small" type="time" label="End" value={f.endTime}
            onChange={e => set({ endTime: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>Courts</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <ToggleButtonGroup size="small" value={f.courts} onChange={(e, v) => set({ courts: v })}>
                {COURTS.map(c => <ToggleButton key={c} value={c} sx={{ px: 1.5 }}>{c.replace('court-', '')}</ToggleButton>)}
              </ToggleButtonGroup>
              <Button size="small" onClick={() => set({ courts: COURTS.slice(0, 3) })}>1–3</Button>
              <Button size="small" onClick={() => set({ courts: COURTS.slice(3) })}>4–6</Button>
              <Button size="small" onClick={() => set({ courts: [...COURTS] })}>All 6</Button>
            </Box>
          </Grid>

          {isNew && f.kind === 'practice' && (
            <Grid size={{ xs: 12 }}>
              <FormControlLabel label="Repeat weekly"
                control={<Checkbox checked={f.repeat.on} onChange={e => setRepeat({ on: e.target.checked })} />} />
              {f.repeat.on && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <ToggleButtonGroup size="small" value={f.repeat.days} onChange={(e, v) => setRepeat({ days: v })}>
                    {WEEKDAYS.map(d => <ToggleButton key={d} value={d} sx={{ px: 1.2 }}>{DAY_SHORT[d]}</ToggleButton>)}
                  </ToggleButtonGroup>
                  <TextField size="small" type="date" label="Until" value={f.repeat.until}
                    onChange={e => setRepeat({ until: e.target.value })} InputLabelProps={{ shrink: true }} />
                  <TextField size="small" sx={{ minWidth: 260, flex: 1 }} label="Skip dates (spring break…)" placeholder="2026-03-16, 2026-03-17"
                    value={f.repeat.skip} onChange={e => setRepeat({ skip: e.target.value })} />
                </Box>
              )}
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField select fullWidth size="small" label="Billing" value={f.billing.mode} onChange={e => setBilling({ mode: e.target.value })}>
              {Object.entries(BILLING).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
          </Grid>
          {charged && (
            <>
              <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="number" label="Per court-hour" value={f.billing.rate}
                onChange={e => setBilling({ rate: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  {f.courts.length} × {courtHours({ ...f, courts: [1] })}h = <strong>{fmtMoney(amountCents)}</strong>{dates.length > 1 ? ' each' : ''}
                </Typography>
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12 }}><TextField fullWidth size="small" multiline minRows={2} label="Notes" value={f.notes}
            onChange={e => set({ notes: e.target.value })} /></Grid>
        </Grid>

        {repeating && <Alert severity="info" sx={{ mt: 2 }}>{dates.length} practice{dates.length === 1 ? '' : 's'} will be added.</Alert>}
        {clashes.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Overlaps {clashes.length} other session{clashes.length === 1 ? '' : 's'} on the same court:{' '}
            {clashes.slice(0, 4).map(c => `${c.schoolCode} ${shortDate(c.date)} ${clock(c.startTime)}`).join('; ')}{clashes.length > 4 ? '…' : ''}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {!isNew && <Button color="error" startIcon={<DeleteIcon />} onClick={() => remove(false)} disabled={busy}>Delete</Button>}
        {!isNew && f.seriesId && <Button color="error" onClick={() => remove(true)} disabled={busy}>Delete this and later</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !valid} sx={{ backgroundColor: navy }}>
          {busy ? 'Saving…' : isNew ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

